import { useEffect, useState } from 'react'
import { ActionIcon, Button, Popover, Tooltip } from '@mantine/core'
import { IconColumns, IconEye, IconEyeOff, IconGripVertical } from '@tabler/icons-react'
import { Reorder, useDragControls, useReducedMotion } from 'motion/react'
import { orderedTableColumns, tableColumnOrder } from '@/lib/tableColumns.mjs'
import ColumnInfoContent from './ColumnInfoContent'

function ColumnRow({ column, selected, toggle, move, commit, reorderable }) {
    const controls = useDragControls()
    const reducedMotion = useReducedMotion()
    const Row = reorderable ? Reorder.Item : 'div'
    const dragProps = reorderable ? {
        value: column.key, dragListener: false, dragControls: controls,
        onDragEnd: commit, transition: reducedMotion ? { duration: 0 } : undefined,
        whileDrag: { zIndex: 1, boxShadow: '0 2px 8px #0000001a' },
    } : {}
    return <Row {...dragProps} className="relative flex items-center gap-1 rounded-md bg-white px-1 py-1 hover:bg-gray-50">
        {reorderable && <ActionIcon type="button" size={36} variant="subtle" color="gray" className="shrink-0 touch-none cursor-grab"
            aria-label={`Déplacer ${column.name}`} title="Glisser ou utiliser les flèches haut et bas"
            onPointerDown={(event) => controls.start(event)}
            onKeyDown={(event) => {
                if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                    event.preventDefault()
                    move(column.key, event.key === 'ArrowUp' ? -1 : 1)
                }
            }}><IconGripVertical size={18} /></ActionIcon>}
        <Tooltip disabled={!column.tooltip && !column.formula}
            label={<ColumnInfoContent tooltip={column.tooltip} formula={column.formula} />}
            position="bottom" color="white" multiline withinPortal openDelay={400}
            events={{ hover: true, focus: true, touch: true }}
            styles={{ tooltip: { maxWidth: 'calc(100vw - 32px)', padding: 12, border: '1px solid #e5e7eb', boxShadow: '0 4px 12px #0000001a' } }}>
            <span tabIndex={column.tooltip || column.formula ? 0 : undefined}
                className={`min-w-0 flex-1 truncate rounded text-sm ${selected ? 'text-gray-900' : 'text-gray-500'}`}>{column.name}</span>
        </Tooltip>
        <ActionIcon type="button" size={36} variant="subtle" color="gray" radius="xl" className="shrink-0"
            aria-label={`Afficher ${column.name}`} aria-pressed={selected}
            title={`${selected ? 'Masquer' : 'Afficher'} la colonne`} onClick={toggle}>
            {selected ? <IconEye size={19} /> : <IconEyeOff size={19} />}
        </ActionIcon>
    </Row>
}

export default function TableColumns({ availableColumns = [], tableState, changeColumns, withinPortal = true, triggerLabel, inline = false, reorderable = true }) {
    const [opened, setOpened] = useState(false)
    const optional = availableColumns.filter((column) => ['optional', 'optional_default'].includes(column.display))
    const orderKey = JSON.stringify(tableColumnOrder(optional, tableState.columns, tableState.columnOrder))
    const [order, setOrder] = useState(() => JSON.parse(orderKey))
    useEffect(() => setOrder(JSON.parse(orderKey)), [orderKey])
    const selectedKeys = new Set(orderedTableColumns(availableColumns, tableState.columns, tableState.columnOrder).map((column) => column.key))
    const commit = (nextOrder = order, selected = selectedKeys) => {
        changeColumns([...availableColumns.filter((column) => column.display === 'mandatory').map((column) => column.key), ...nextOrder.filter((key) => selected.has(key))], nextOrder)
    }
    const move = (key, offset) => {
        const index = order.indexOf(key)
        const target = index + offset
        if (target < 0 || target >= order.length) return
        const next = [...order]
        next.splice(target, 0, next.splice(index, 1)[0])
        setOrder(next)
        commit(next)
    }
    if (!optional.length) return null
    const rows = order.map((key) => {
        const column = optional.find((item) => item.key === key)
        if (!column) return null
        return <ColumnRow key={key} column={column} selected={selectedKeys.has(key)} move={move} commit={() => commit()} reorderable={reorderable}
            toggle={() => {
                const next = new Set(selectedKeys)
                if (next.has(key)) next.delete(key)
                else next.add(key)
                commit(order, next)
            }} />
    })
    const scrollClass = `${inline ? 'max-h-[320px]' : 'max-h-[min(520px,65dvh)]'} overflow-y-auto overscroll-contain`
    const content = (
        <div role="region" aria-label="Colonnes">
            {reorderable ? <Reorder.Group axis="y" values={order} onReorder={setOrder} layoutScroll className={scrollClass}>{rows}</Reorder.Group> : <div className={scrollClass}>{rows}</div>}
        </div>
    )
    if (inline) return content

    return <Popover opened={opened} onChange={setOpened} position="bottom-end" width={360} shadow="md" radius="lg" withinPortal={withinPortal} returnFocus>
        <Popover.Target>
            {triggerLabel ? <Button variant="default" size="compact-sm" onClick={() => setOpened((value) => !value)}>{triggerLabel}</Button> : <Tooltip label="Colonnes" disabled={opened} events={{ hover: true, focus: true, touch: false }}><ActionIcon size={35} variant={opened ? 'light' : 'subtle'} color="gray" radius="xl" aria-label="Gérer les colonnes" onClick={() => setOpened((value) => !value)}><IconColumns size={19} /></ActionIcon></Tooltip>}
        </Popover.Target>
        <Popover.Dropdown p={8} style={{ maxWidth: 'calc(100vw - 24px)' }}>
            {content}
        </Popover.Dropdown>
    </Popover>
}
